// frontend/src/components/LanguageSwitcher.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n/index';
import {
    getLanguageInfo,
    getSupportedLanguages
} from '../utils/languageDetection';
import { ChevronDown } from 'lucide-react';

const LanguageSwitcher = ({ variant = 'compact', className = '' }) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [currentLang, setCurrentLang] = useState(i18n.language?.split('-')[0] || 'en');
    const dropdownRef = useRef(null);

    const languages = getSupportedLanguages();
    const currentLanguageInfo = getLanguageInfo(currentLang);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Sync with i18n language changes (e.g. from other tabs)
    useEffect(() => {
        const onChanged = (lng) => {
            setCurrentLang(lng.split('-')[0]);
        };
        i18n.on('languageChanged', onChanged);
        return () => i18n.off('languageChanged', onChanged);
    }, []);

    const handleSelect = async (langCode) => {
        try {
            const normalizedCode = langCode.toLowerCase();
            console.log('[Switcher] Selecting:', normalizedCode);

            await i18n.changeLanguage(normalizedCode);
            localStorage.setItem('gigship_language', normalizedCode);
            document.documentElement.lang = normalizedCode;
            document.documentElement.dir = normalizedCode === 'ar' ? 'rtl' : 'ltr';

            console.log('[Switcher] Done. Current:', i18n.language);
            setIsOpen(false);

            // Sync with backend if logged in
            const token = localStorage.getItem('gigship_token');
            if (token) {
                fetch(`${import.meta.env.VITE_API_URL}/users/profile`, {
                    method: 'PATCH',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ preferredLanguage: normalizedCode })
                }).catch(err => console.error('Failed to sync language to backend', err));
            }
        } catch (error) {
            console.error('Language switch failed:', error);
        }
    };


    if (!currentLanguageInfo) return null;

    return (
        <div ref={dropdownRef} className={`relative inline-block ${className}`}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:border-blue-400 hover:bg-gray-50 transition-all shadow-sm"
                aria-label="Change language"
            >
                <span className="text-lg leading-none" role="img">
                    {currentLanguageInfo.flag}
                </span>
                <span className="font-semibold uppercase">{currentLanguageInfo.code}</span>
                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {isOpen && (
                <div className="absolute right-0 mt-2 w-64 bg-white border border-gray-200 rounded-xl shadow-xl z-[100] py-2 max-h-96 overflow-y-auto">
                    <div className="px-4 py-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-widest border-b border-gray-50 mb-1">
                        {t('select_language', 'Select Language')}
                    </div>
                    {languages.map((lang) => (
                        <button
                            key={lang.code}
                            onClick={() => handleSelect(lang.code)}
                            className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left transition-colors ${lang.code === currentLang
                                ? 'bg-blue-50 text-blue-700 font-semibold'
                                : 'text-gray-700 hover:bg-gray-50'
                                }`}
                        >
                            <span className="text-xl leading-none" role="img">
                                {lang.flag}
                            </span>
                            <div className="flex flex-col">
                                <span className="text-sm font-medium">{lang.nativeName}</span>
                                <span className="text-[10px] text-gray-400">{lang.name}</span>
                            </div>
                            {lang.code === currentLang && (
                                <div className="ml-auto w-2 h-2 rounded-full bg-blue-600"></div>
                            )}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default LanguageSwitcher;